import { AxiosError } from 'axios';
import { useQuery } from '@tanstack/react-query';
import React, { useEffect, useImperativeHandle, useState } from 'react';
import { Button, Col, Form, Row } from 'react-bootstrap';
import { toast } from 'react-toastify';
import CustomModal from 'components/modals/CustomModal';
import axiosInstance from 'utils/axiosInterceptors';
import { toastConfig } from 'utils/toastConfig';
import { BranchFunds, FundBalance, formatPesos } from './expenseOptions';

type FundMode = 'add' | 'set' | 'create';

export type FundFormHandle = {
  submitForm: () => Promise<boolean>;
};

type FundFormProps = {
  branchDomain: string;
  mode: FundMode;
  fund: FundBalance | null;
  openToken: number;
  onSaved: () => void;
};

const todayInput = () => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
};

const apiError = (error: unknown, fallback: string) => {
  if (error instanceof AxiosError) {
    const data = error.response?.data as { errorMessage?: string } | undefined;
    return data?.errorMessage || fallback;
  }
  return fallback;
};

const FundForm = React.forwardRef<FundFormHandle, FundFormProps>(
  ({ branchDomain, mode, fund, openToken, onSaved }, ref) => {
    const [entryDate, setEntryDate] = useState(todayInput());
    const [amount, setAmount] = useState('');
    const [note, setNote] = useState('');
    const [name, setName] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const fundName = fund?.name ?? 'fund';

    useEffect(() => {
      setEntryDate(todayInput());
      setAmount('');
      setNote('');
      setName('');
    }, [openToken, mode, fund?.fundId]);

    const submitForm = async () => {
      if (isSaving) return false;

      if (mode === 'create') {
        if (!name.trim()) {
          toast.error('Fund name is required.', { ...toastConfig, autoClose: 3000 });
          return false;
        }

        try {
          setIsSaving(true);
          await axiosInstance.post(
            `/api/v1/${branchDomain}/operating-expenses/funds`,
            { name: name.trim() }
          );
          toast.success('Fund added.', { ...toastConfig, autoClose: 2000 });
          onSaved();
          return true;
        } catch (error) {
          toast.error(apiError(error, 'Could not add the fund.'), {
            ...toastConfig,
            autoClose: 3000
          });
          return false;
        } finally {
          setIsSaving(false);
        }
      }

      if (!fund) return false;
      const parsedAmount = Number(amount);
      if (!entryDate) {
        toast.error('Date is required.', { ...toastConfig, autoClose: 3000 });
        return false;
      }
      if (!Number.isFinite(parsedAmount) || (mode === 'add' ? parsedAmount <= 0 : parsedAmount < 0)) {
        toast.error(
          mode === 'add'
            ? 'Amount must be greater than zero.'
            : 'Balance cannot be below zero.',
          { ...toastConfig, autoClose: 3000 }
        );
        return false;
      }

      try {
        setIsSaving(true);
        await axiosInstance.post(
          `/api/v1/${branchDomain}/operating-expenses/funds/${fund.fundId}/${mode}`,
          {
            entryDate,
            amount: parsedAmount,
            note: note.trim() || null
          }
        );
        toast.success(
          mode === 'add' ? `Added to ${fundName}.` : `${fundName} updated.`,
          { ...toastConfig, autoClose: 2000 }
        );
        onSaved();
        return true;
      } catch (error) {
        toast.error(apiError(error, `Could not update ${fundName}.`), {
          ...toastConfig,
          autoClose: 3000
        });
        return false;
      } finally {
        setIsSaving(false);
      }
    };

    useImperativeHandle(ref, () => ({ submitForm }));

    if (mode === 'create') {
      return (
        <Form>
          <Form.Label>Fund name</Form.Label>
          <Form.Control
            value={name}
            maxLength={80}
            placeholder="Petty cash drawer"
            onChange={event => setName(event.target.value)}
          />
          <div className="text-body-tertiary fs-9 mt-2">
            A new fund starts at zero for every branch. Add money to it the
            same way as the shop fund or the cash fund.
          </div>
        </Form>
      );
    }

    return (
      <Form>
        <Row className="g-3">
          <Col md={6}>
            <Form.Label>Date</Form.Label>
            <Form.Control
              type="date"
              value={entryDate}
              onChange={event => setEntryDate(event.target.value)}
            />
          </Col>
          <Col md={6}>
            <Form.Label>{mode === 'add' ? 'Amount to add' : 'New balance'}</Form.Label>
            <Form.Control
              type="number"
              min="0"
              step="0.01"
              value={amount}
              onChange={event => setAmount(event.target.value)}
            />
          </Col>
          <Col md={12}>
            <Form.Label>Note</Form.Label>
            <Form.Control
              value={note}
              maxLength={500}
              placeholder={mode === 'add' ? `Added to ${fundName}` : `Set ${fundName}`}
              onChange={event => setNote(event.target.value)}
            />
            <div className="text-body-tertiary fs-9 mt-2">
              {mode === 'add'
                ? `This credits the ${fundName}.`
                : 'This records the difference so the balance matches the amount you enter.'}
            </div>
          </Col>
        </Row>
      </Form>
    );
  }
);

type FundsPanelProps = {
  branchDomain: string;
  refreshKey: number;
  onChanged: () => void;
};

const FundsPanel = ({ branchDomain, refreshKey, onChanged }: FundsPanelProps) => {
  const [mode, setMode] = useState<FundMode>('add');
  const [selected, setSelected] = useState<FundBalance | null>(null);
  const [openToken, setOpenToken] = useState(0);
  const formRef = React.useRef<FundFormHandle>(null);
  const modalRef = React.useRef<{ show: () => void; hide: () => void }>({
    show: () => undefined,
    hide: () => undefined
  });

  const fundsQuery = useQuery({
    queryKey: ['operating-funds', branchDomain, refreshKey],
    enabled: Boolean(branchDomain),
    queryFn: async () => {
      const { data } = await axiosInstance.get<BranchFunds>(
        `/api/v1/${branchDomain}/operating-expenses/funds`
      );
      return data;
    }
  });

  const funds = fundsQuery.data?.funds ?? [];

  const open = (nextMode: FundMode, fund: FundBalance | null) => {
    setMode(nextMode);
    setSelected(fund);
    setOpenToken(token => token + 1);
    modalRef.current.show();
  };

  const save = async () => {
    const saved = await formRef.current?.submitForm();
    if (saved) modalRef.current.hide();
  };

  const title =
    mode === 'create'
      ? 'New fund'
      : mode === 'add'
        ? `Add to ${selected?.name ?? 'fund'}`
        : `Set ${selected?.name ?? 'fund'}`;

  return (
    <div className="mb-3">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div>
          <div className="fw-semibold">Funds</div>
          <div className="text-body-tertiary fs-9">
            Each fund has its own balance. Expenses paid from a fund come out of that balance.
          </div>
        </div>
        <Button variant="phoenix-secondary" onClick={() => open('create', null)}>
          New fund
        </Button>
      </div>
      <div className="d-flex flex-wrap gap-2">
        {funds.map(fund => (
          <div
            key={fund.fundId}
            className="border rounded p-3 bg-body"
            style={{ minWidth: 240, flex: '1 1 240px' }}
          >
            <div className="fs-10 text-body-tertiary">{fund.name}</div>
            <div className="fw-bold fs-6">{formatPesos(fund.balance)}</div>
            <div className="text-body-tertiary fs-9 mb-2">
              Credits {formatPesos(fund.totalCredits)} · Debits {formatPesos(fund.totalDebits)}
            </div>
            <div className="d-flex gap-2">
              <Button
                size="sm"
                variant="phoenix-secondary"
                onClick={() => open('set', fund)}
              >
                Set
              </Button>
              <Button size="sm" variant="primary" onClick={() => open('add', fund)}>
                Add
              </Button>
            </div>
          </div>
        ))}
        {!fundsQuery.isLoading && funds.length === 0 ? (
          <div className="text-body-tertiary fs-9">No funds yet.</div>
        ) : null}
      </div>

      <CustomModal
        ref={modalRef}
        title={title}
        onSubmitCustomDisplay="Save"
        onSubmit={save}
        content={
          <FundForm
            ref={formRef}
            branchDomain={branchDomain}
            mode={mode}
            fund={selected}
            openToken={openToken}
            onSaved={onChanged}
          />
        }
      />
    </div>
  );
};

export default FundsPanel;
